import React, { useState, useEffect } from 'react';
import { 
  Card, Row, Col, Statistic, Typography, Layout, theme,
  Table, Tag, Progress, Space, Avatar, Tooltip, Radio,
  Button, Spin, FloatButton
} from 'antd';
import { 
  FaPhoneAlt, FaChartLine, FaUsers, FaMapMarkerAlt,
  FaCalendarAlt, FaChartBar, FaChartArea, FaQuestionCircle,
  FaExclamationTriangle, FaClock, FaCalendarWeek,
  FaGlobeAmericas, FaCity, FaDatabase, FaRegChartBar,
  FaSync
} from 'react-icons/fa';
import { 
  PhoneOutlined, UserOutlined, EnvironmentOutlined,
  CalendarOutlined, ClockCircleOutlined, RiseOutlined,
  BarChartOutlined, LineChartOutlined, PieChartOutlined,
  WhatsAppOutlined, WarningOutlined, SyncOutlined,
  ReloadOutlined, UpOutlined, DashboardOutlined
} from '@ant-design/icons';
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Legend
} from 'recharts';
import api from '../api/Api';

const { Title, Text } = Typography;
const { Content } = Layout;
const { useToken } = theme;

const Dashboard = () => {
  const { token } = useToken();
  
  const [dashboardData, setDashboardData] = useState({
    totals: { total: 0, laee: 0, engano: 0 },
    topProvincias: [],
    topMunicipios: [],
    topPerguntas: [],
    horasData: [],
    mesesData: [],
    anosData: []
  });
  const [timeRange, setTimeRange] = useState('hoje');
  const [loading, setLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    document.title = "TECH NEXT CRM - DASHBOARD";
    fetchDashboardData();
    
    // Atualizar dados a cada 60 segundos
    const interval = setInterval(fetchDashboardData, 60000);
    return () => clearInterval(interval);
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      const [
        totals,
        provincias,
        municipios,
        perguntas,
        horas,
        meses,
        anos
      ] = await Promise.all([
        api.TotalDashboard(),
        api.Top5Provincia(),
        api.Top5Municipio(),
        api.Top5Pergunta(),
        api.Dashboardhora(),
        api.Dashboardmes(),
        api.Dashboardano()
      ]);

      setDashboardData({
        totals: totals || { total: 0, laee: 0, engano: 0 },
        topProvincias: provincias || [],
        topMunicipios: municipios || [],
        topPerguntas: perguntas || [],
        horasData: processHorasData(horas || []),
        mesesData: processMesesData(meses || []),
        anosData: anos || []
      });
      
      setLastUpdated(new Date());
    } catch (error) {
      console.error("Erro ao buscar dados do dashboard:", error);
    } finally {
      setLoading(false);
    }
  };

  const processHorasData = (data) => {
    const horasCompletas = Array.from({ length: 24 }, (_, i) => ({
      hora: `${i.toString().padStart(2, '0')}:00`,
      total: 0
    }));

    data.forEach(item => {
      if (item.hora >= 0 && item.hora <= 23) {
        horasCompletas[item.hora].total = item.total;
      }
    });

    return horasCompletas;
  };

  const processMesesData = (data) => {
    const meses = [
      'Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun',
      'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'
    ];

    const mesesCompletos = meses.map((mes, index) => ({
      mes,
      total: 0
    }));

    data.forEach(item => {
      if (item.mes >= 1 && item.mes <= 12) {
        mesesCompletos[item.mes - 1].total = item.total;
      }
    });

    return mesesCompletos;
  };

  const COLORS = [
    token.colorPrimary, token.colorSuccess, token.colorWarning,
    token.colorError, token.colorInfo, token.colorProcessing,
    '#722ED1', '#13C2C2', '#F5222D', '#FA8C16', '#52C41A'
  ];

  const gradientColor = token.colorPrimary;

  // Header com título e botão de atualizar
 

  // Estatísticas principais
  const StatCards = () => (
    <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
      <Col xs={24} sm={12} lg={8}>
        <Card 
          hoverable
          style={{
            background: `linear-gradient(135deg, ${token.colorPrimary} 0%, ${token.colorPrimaryActive} 100%)`,
            borderRadius: '12px',
            border: 'none',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
            height: '100%'
          }}
          bodyStyle={{ padding: '20px' }}
        >
          <Statistic
            title={
              <Space style={{ color: 'white', opacity: 0.9 }}>
                <PhoneOutlined />
                <Text style={{ color: 'white' }}>Total Chamadas</Text>
              </Space>
            }
            value={dashboardData.totals.total}
            valueStyle={{ 
              color: 'white',
              fontSize: '32px',
              fontWeight: 'bold'
            }}
            prefix={<FaPhoneAlt />}
            suffix={
              <Tag color="white" style={{ color: token.colorPrimary, fontSize: '12px' }}>
                +12% <RiseOutlined />
              </Tag>
            }
          />
          <div style={{ marginTop: '12px' }}>
            <Progress 
              percent={75}
              strokeColor="white"
              trailColor="rgba(255, 255, 255, 0.2)"
              showInfo={false}
              size="small"
            />
          </div>
        </Card>
      </Col>

      <Col xs={24} sm={12} lg={8}>
        <Card 
          hoverable
          style={{
            background: `linear-gradient(135deg, ${token.colorSuccess} 0%, ${token.colorSuccessActive} 100%)`,
            borderRadius: '12px',
            border: 'none',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
            height: '100%'
          }}
          bodyStyle={{ padding: '20px' }}
        >
          <Statistic
            title={
              <Space style={{ color: 'white', opacity: 0.9 }}>
                <FaChartLine />
                <Text style={{ color: 'white' }}>Chamadas LAEE</Text>
              </Space>
            }
            value={dashboardData.totals.laee}
            valueStyle={{ 
              color: 'white',
              fontSize: '32px',
              fontWeight: 'bold'
            }}
            prefix={<FaRegChartBar />}
            suffix={
              <Tag color="white" style={{ color: token.colorSuccess, fontSize: '12px' }}>
                {dashboardData.totals.total > 0 
                  ? `${Math.round((dashboardData.totals.laee / dashboardData.totals.total) * 100)}%`
                  : '0%'}
              </Tag>
            }
          />
          <div style={{ marginTop: '12px' }}>
            <Progress 
              percent={dashboardData.totals.total > 0 
                ? (dashboardData.totals.laee / dashboardData.totals.total) * 100 
                : 0}
              strokeColor="white"
              trailColor="rgba(255, 255, 255, 0.2)"
              showInfo={false}
              size="small"
            />
          </div>
        </Card>
      </Col>

      <Col xs={24} sm={12} lg={8}>
        <Card 
          hoverable
          style={{
            background: `linear-gradient(135deg, ${token.colorWarning} 0%, ${token.colorWarningActive} 100%)`,
            borderRadius: '12px',
            border: 'none',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
            height: '100%'
          }}
          bodyStyle={{ padding: '20px' }}
        >
          <Statistic
            title={
              <Space style={{ color: 'white', opacity: 0.9 }}>
                <WarningOutlined />
                <Text style={{ color: 'white' }}>Chamadas Engano</Text>
              </Space>
            }
            value={dashboardData.totals.engano}
            valueStyle={{ 
              color: 'white',
              fontSize: '32px',
              fontWeight: 'bold'
            }}
            prefix={<FaExclamationTriangle />}
            suffix={
              <Tag color="white" style={{ color: token.colorWarning, fontSize: '12px' }}>
                {dashboardData.totals.total > 0 
                  ? `${Math.round((dashboardData.totals.engano / dashboardData.totals.total) * 100)}%`
                  : '0%'}
              </Tag>
            }
          />
          <div style={{ marginTop: '12px' }}>
            <Progress 
              percent={dashboardData.totals.total > 0 
                ? (dashboardData.totals.engano / dashboardData.totals.total) * 100 
                : 0}
              strokeColor="white"
              trailColor="rgba(255, 255, 255, 0.2)"
              showInfo={false}
              size="small"
            />
          </div>
        </Card>
      </Col>
    </Row>
  );

  // Gráficos
  const ChamadasPorHoraChart = () => (
    <Card
      title={
        <Space>
          <ClockCircleOutlined />
          <span>Chamadas por Hora do Dia</span>
        </Space>
      }
      
      style={{ borderRadius: '12px', marginBottom: '24px' }}
      hoverable
    >
      <div style={{ height: '300px' }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={dashboardData.horasData}
            margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
          >
            <defs>
              <linearGradient id="colorHora" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={gradientColor} stopOpacity={0.8}/>
                <stop offset="95%" stopColor={gradientColor} stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={token.colorBorder} />
            <XAxis 
              dataKey="hora" 
              tick={{ fill: token.colorTextSecondary }}
              axisLine={{ stroke: token.colorBorder }}
            />
            <YAxis 
              tick={{ fill: token.colorTextSecondary }}
              axisLine={{ stroke: token.colorBorder }}
            />
            <RechartsTooltip 
              contentStyle={{ 
                backgroundColor: token.colorBgContainer,
                border: `1px solid ${token.colorBorder}`,
                borderRadius: '8px'
              }}
              labelStyle={{ color: token.colorPrimary }}
            />
            <Area 
              type="monotone" 
              dataKey="total" 
              stroke={token.colorPrimary}
              fillOpacity={1}
              fill="url(#colorHora)"
              strokeWidth={2}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );

  const ChamadasPorMesChart = () => (
    <Card
      title={
        <Space>
          <CalendarOutlined />
          <span>Chamadas por Mês</span>
        </Space>
      }
      style={{ borderRadius: '12px', marginBottom: '24px' }}
      hoverable
    >
      <div style={{ height: '300px' }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={dashboardData.mesesData}
            margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke={token.colorBorder} />
            <XAxis 
              dataKey="mes" 
              tick={{ fill: token.colorTextSecondary }}
              axisLine={{ stroke: token.colorBorder }}
            />
            <YAxis 
              tick={{ fill: token.colorTextSecondary }}
              axisLine={{ stroke: token.colorBorder }}
            />
            <RechartsTooltip 
              contentStyle={{ 
                backgroundColor: token.colorBgContainer,
                border: `1px solid ${token.colorBorder}`,
                borderRadius: '8px'
              }}
            />
            <Line 
              type="monotone" 
              dataKey="total" 
              stroke={token.colorSuccess}
              strokeWidth={3}
              dot={{ fill: token.colorSuccess, strokeWidth: 2, r: 4 }}
              activeDot={{ r: 6, fill: token.colorSuccessActive }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );

  const ChamadasPorAnoChart = () => (
    <Card
      title={
        <Space>
          <FaChartBar />
          <span>Chamadas por Ano</span>
        </Space>
      }
      style={{ borderRadius: '12px', marginBottom: '24px' }}
      hoverable
    >
      <div style={{ height: '300px' }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={dashboardData.anosData}
            margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke={token.colorBorder} />
            <XAxis 
              dataKey="ano" 
              tick={{ fill: token.colorTextSecondary }}
              axisLine={{ stroke: token.colorBorder }}
            />
            <YAxis 
              tick={{ fill: token.colorTextSecondary }}
              axisLine={{ stroke: token.colorBorder }}
            />
            <RechartsTooltip 
              contentStyle={{ 
                backgroundColor: token.colorBgContainer,
                border: `1px solid ${token.colorBorder}`,
                borderRadius: '8px'
              }}
            />
            <Bar 
              dataKey="total" 
              fill={token.colorPrimary}
              radius={[4, 4, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );

  // Tabelas Top 5
  const TopProvinciasTable = () => {
    const columns = [
      {
        title: 'Rank',
        dataIndex: 'rank',
        key: 'rank',
        width: 60,
        render: (_, record, index) => (
          <Avatar 
            size="small"
            style={{
              backgroundColor: COLORS[index % COLORS.length],
              color: 'white',
              fontWeight: 'bold'
            }}
          >
            {index + 1}
          </Avatar>
        ),
      },
      {
        title: 'Província',
        dataIndex: 'provincia',
        key: 'provincia',
        render: (text) => (
          <Space>
            <EnvironmentOutlined style={{ color: token.colorSuccess }} />
            {text}
          </Space>
        ),
      },
      {
        title: 'Total Chamadas',
        dataIndex: 'total',
        key: 'total',
        render: (total) => (
          <Tag color="blue" style={{ fontWeight: 'bold' }}>
            {total}
          </Tag>
        ),
      },
 
    ];

    return (
      <Card
        title={
          <Space>
            <FaGlobeAmericas />
            <span>Top 5 Províncias</span>
          </Space>
        }
        extra={<FaMapMarkerAlt style={{ color: token.colorPrimary }} />}
        style={{ borderRadius: '12px', height: '100%' }}
        hoverable
      >
        <Table
          columns={columns}
          dataSource={dashboardData.topProvincias.map((item, index) => ({
            ...item,
            key: index,
            rank: index + 1
          }))}
          pagination={false}
          size="small"
          loading={loading}
          scroll={{ y: 240 }}
        />
      </Card>
    );
  };

  const TopMunicipiosTable = () => {
    const columns = [
      {
        title: 'Rank',
        dataIndex: 'rank',
        key: 'rank',
        width: 60,
        render: (_, record, index) => (
          <Avatar 
            size="small"
            style={{
              backgroundColor: COLORS[(index + 2) % COLORS.length],
              color: 'white',
              fontWeight: 'bold'
            }}
          >
            {index + 1}
          </Avatar>
        ),
      },
      {
        title: 'Município',
        dataIndex: 'municipio',
        key: 'municipio',
        render: (text) => (
          <Space>
            <FaCity style={{ color: token.colorPrimary }} />
            {text}
          </Space>
        ),
      },
      {
        title: 'Total Chamadas',
        dataIndex: 'total',
        key: 'total',
        render: (total) => (
          <Tag color="green" style={{ fontWeight: 'bold' }}>
            {total}
          </Tag>
        ),
      },
 
    ];

    return (
      <Card
        title={
          <Space>
            <FaCity />
            <span>Top 5 Municípios</span>
          </Space>
        }
        extra={<EnvironmentOutlined style={{ color: token.colorPrimary }} />}
        style={{ borderRadius: '12px', height: '100%' }}
        hoverable
      >
        <Table
          columns={columns}
          dataSource={dashboardData.topMunicipios.map((item, index) => ({
            ...item,
            key: index,
            rank: index + 1
          }))}
          pagination={false}
          size="small"
          loading={loading}
          scroll={{ y: 240 }}
        />
      </Card>
    );
  };

  const TopPerguntasTable = () => {
    const columns = [
      {
        title: 'Rank',
        dataIndex: 'rank',
        key: 'rank',
        width: 60,
        render: (_, record, index) => (
          <Avatar 
            size="small"
            style={{
              backgroundColor: COLORS[(index + 4) % COLORS.length],
              color: 'white',
              fontWeight: 'bold'
            }}
          >
            {index + 1}
          </Avatar>
        ),
      },
      {
        title: 'Pergunta',
        dataIndex: 'pergunta',
        key: 'pergunta',
        ellipsis: true,
        render: (text) => (
          <Tooltip title={text}>
            <Space>
              <FaQuestionCircle style={{ color: token.colorWarning }} />
              <Text style={{ fontSize: '12px' }}>{text}</Text>
            </Space>
          </Tooltip>
        ),
      },
      {
        title: 'Total',
        dataIndex: 'total',
        key: 'total',
        width: 80,
        render: (total) => (
          <Tag color="orange" style={{ fontWeight: 'bold' }}>
            {total}
          </Tag>
        ),
      },
    ];

    return (
      <Card
        title={
          <Space>
            <FaQuestionCircle />
            <span>Top 5 Perguntas</span>
          </Space>
        }
        extra={<FaDatabase style={{ color: token.colorPrimary }} />}
        style={{ borderRadius: '12px', height: '100%' }}
        hoverable
      >
        <Table
          columns={columns}
          dataSource={dashboardData.topPerguntas.map((item, index) => ({
            ...item,
            key: index,
            rank: index + 1
          }))}
          pagination={false}
          size="small"
          loading={loading}
          scroll={{ y: 240 }}
        />
      </Card>
    );
  };

  // KPIs adicionais
  const AdditionalKPIs = () => (
    <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
      <Col xs={24} sm={12} md={6}>
        <Card size="small" hoverable>
          <Statistic
            title="Média Diária"
            value={dashboardData.totals.total > 0 ? Math.round(dashboardData.totals.total / 30) : 0}
            prefix={<FaCalendarWeek />}
            suffix="chamadas"
            valueStyle={{ color: token.colorPrimary }}
          />
        </Card>
      </Col>
      <Col xs={24} sm={12} md={6}>
        <Card size="small" hoverable>
          <Statistic
            title="Pico Horário"
            value={
              dashboardData.horasData.length > 0
                ? dashboardData.horasData.reduce((max, item) => 
                    item.total > max.total ? item : max
                  ).hora
                : 'N/A'
            }
            prefix={<FaClock />}
            valueStyle={{ color: token.colorSuccess }}
          />
        </Card>
      </Col>
      <Col xs={24} sm={12} md={6}>
        <Card size="small" hoverable>
          <Statistic
            title="Taxa de Engano"
            value={
              dashboardData.totals.total > 0
                ? `${Math.round((dashboardData.totals.engano / dashboardData.totals.total) * 100)}%`
                : '0%'
            }
            prefix={<FaExclamationTriangle />}
            valueStyle={{ color: token.colorWarning }}
          />
        </Card>
      </Col>
      <Col xs={24} sm={12} md={6}>
        <Card size="small" hoverable>
          <Statistic
            title="Status"
            value={loading ? "Atualizando..." : "Atualizado"}
            prefix={<SyncOutlined spin={loading} />}
            valueStyle={{ color: loading ? token.colorWarning : token.colorSuccess }}
          />
        </Card>
      </Col>
    </Row>
  );

  return (
    <Layout style={{ minHeight: '100vh', background: token.colorBgLayout }}>
      <Content style={{ padding: '24px', overflow: 'auto', height: '100vh' }}>
        {loading && (
          <div style={{ 
            position: 'fixed', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            background: 'rgba(255, 255, 255, 0.7)', 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            zIndex: 1000 
          }}>
            <Spin size="large" tip="Carregando dados do dashboard..." />
          </div>
        )}
        
        <div style={{ paddingBottom: '50px' }}>
       
          
          {/* Estatísticas Principais */}
          <StatCards />
          
          {/* Gráficos em linha única */}
          <Row gutter={[16, 16]}>
            <Col xs={24}>
              <ChamadasPorHoraChart />
            </Col>
          </Row>
          
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={12}>
              <ChamadasPorMesChart />
            </Col>
            <Col xs={24} lg={12}>
              <ChamadasPorAnoChart />
            </Col>
          </Row>
          
          {/* Tabelas Top 5 */}
          <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
            <Col xs={24} md={8}>
              <TopProvinciasTable />
            </Col>
            <Col xs={24} md={8}>
              <TopMunicipiosTable />
            </Col>
            <Col xs={24} md={8}>
              <TopPerguntasTable />
            </Col>
          </Row>
          
       
          
          {/* Informação de atualização */}
          <div style={{ 
            textAlign: 'center', 
            marginTop: '32px', 
            padding: '16px',
            background: token.colorFillAlter,
            borderRadius: '8px'
          }}>
     
          </div>
        </div>

        {/* Botões flutuantes */}
        <FloatButton.BackTop 
          icon={<UpOutlined />}
          style={{ right: 24, bottom: 24 }}
          visibilityHeight={300}
        />
        
        <FloatButton
          icon={<SyncOutlined spin={loading} />}
          onClick={fetchDashboardData}
          tooltip="Atualizar dados manualmente"
          style={{ right: 24, bottom: 80 }}
        />
      </Content>
    </Layout>
  );
};

export default Dashboard;